// src/features/services/media/hooks/mixEngine/bufferTransport/worklets.ts
import { loadRbWasm } from '../rbLoader';
import type { ScheduledStem } from './types';
import type { WorkletWrapper } from '../orchestrator/types';
import { L } from '../../../utils/logger';
import { ctxOk, safeConnectCtx } from './ctxUtil';

/**
 * One-time module loader guard per AudioContext and URL.
 * Prevents duplicate audioCtx.audioWorklet.addModule(url) calls.
 */
const loadedModulePromisesByCtx = new WeakMap<AudioContext, Map<string, Promise<void>>>();

/** Cached WASM */
let rbWasmBuffer: ArrayBuffer | null = null;

/** Optional, small per-channel-config pool (disabled by default). */
const rbNodePoolByCtx = new WeakMap<AudioContext, Map<string, AudioWorkletNode[]>>();
const rbNodeChannels = new WeakMap<AudioWorkletNode, number>();

/* ---------------- Telemetry (optional, lightweight) ---------------- */
type PoolSizes = { cur: number; max: number };
const rbTelem = {
  init: 0,
  engineDispose: 0,
  fullDispose: 0,
  poolAcquire: 0,
  poolRelease: 0,
  poolFullDiscards: 0,
  poolSizes: new Map<string, PoolSizes>(),
};
const engineDisposeCounted = new WeakSet<AudioWorkletNode>();
let lastTelemLog = 0;
function telemEnabled(): boolean {
  try {
    const env = (process as any)?.env?.REACT_APP_MEDIA_RB_TELEM;
    if (env != null) return env === '1' || String(env).toLowerCase() === 'true';
  } catch {}
  try {
    const v = localStorage.getItem('MEDIA_RB_TELEM') ?? localStorage.getItem('MEDIA_RB_TELEMETRY') ?? localStorage.getItem('MEDIA_RB_TELEM_COUNTERS');
    if (v != null) return v === '1' || String(v).toLowerCase() === 'true';
  } catch {}
  return false;
}
function telemLogSnapshot(reason?: string) {
  if (!telemEnabled()) return;
  const now = Date.now();
  if (now - lastTelemLog < 2000) return; // throttle
  lastTelemLog = now;
  try {
    const pool: Record<string, PoolSizes> = {};
    rbTelem.poolSizes.forEach((v, k) => { pool[k] = { cur: v.cur, max: v.max }; });
    L.info('media:transport', 'rb.telem', {
      reason,
      init: rbTelem.init,
      engineDispose: rbTelem.engineDispose,
      fullDispose: rbTelem.fullDispose,
      poolAcquire: rbTelem.poolAcquire,
      poolRelease: rbTelem.poolRelease,
      poolFullDiscards: rbTelem.poolFullDiscards,
      pool,
    });
  } catch {}
}
function telemBumpEngineDispose(node: AudioWorkletNode) {
  if (engineDisposeCounted.has(node)) return;
  engineDisposeCounted.add(node);
  try { rbTelem.engineDispose++; } catch {}
}
function telemUpdatePoolSize(key: string, cur: number) {
  const entry = rbTelem.poolSizes.get(key) || { cur: 0, max: 0 };
  entry.cur = Math.max(0, cur);
  entry.max = Math.max(entry.max, entry.cur);
  rbTelem.poolSizes.set(key, entry);
}

/**
 * Fully dispose an RB AudioWorkletNode and break references for GC.
 * - Ask processor to dispose and free WASM
 * - Remove all event listeners and close MessagePort
 * - Disconnect node and delete back-refs
 */
export function fullyDisposeRbNode(node: AudioWorkletNode | null | undefined, stem?: any) {
  if (!node) return;
  try { node.port.postMessage({ type: 'dispose' }); } catch {}
  try { rbTelem.fullDispose++; } catch {}
  try { (node as any).onprocessorerror = null; } catch {}
  try { node.port.onmessage = null as any; } catch {}
  try { node.port.onmessageerror = null as any; } catch {}
  try { if (stem?._rbOnLive) node.port.removeEventListener('message', stem._rbOnLive); } catch {}
  try { if (stem && '_rbOnLive' in stem) (stem as any)._rbOnLive = undefined; } catch {}
  try { delete (node as any).__stemRef; } catch {}
  try { node.disconnect(); } catch {}
  try { node.port.close(); } catch {}
}

function isRbPoolEnabled(): boolean {
  try {
    const env = (process as any)?.env?.REACT_APP_MEDIA_RB_POOL;
    if (env != null) return env === '1' || String(env).toLowerCase() === 'true';
  } catch {}
  try {
    const v = localStorage.getItem('MEDIA_RB_POOL');
    if (v != null) return v === '1' || String(v).toLowerCase() === 'true';
  } catch {}
  return false; // default OFF to favor memory reclamation
}

function poolMap(ctx: AudioContext): Map<string, AudioWorkletNode[]> {
  let m = rbNodePoolByCtx.get(ctx);
  if (!m) { m = new Map(); rbNodePoolByCtx.set(ctx, m); }
  return m;
}

/**
 * Clear and fully dispose any pooled RB nodes and caches tied to a given AudioContext.
 * Invoke this when a context is closed/replaced to avoid shells lingering across generations.
 */
export function clearWorkletCachesForCtx(ctx: AudioContext | null | undefined) {
  if (!ctx) return;
  try {
    const m = rbNodePoolByCtx.get(ctx);
    if (m) {
      try {
        m.forEach((arr, key) => {
          try {
            for (let i = 0; i < arr.length; i++) {
              try { fullyDisposeRbNode(arr[i]); } catch {}
            }
          } catch {}
          try { telemUpdatePoolSize(key, 0); } catch {}
        });
      } catch {}
      try { m.clear(); } catch {}
      try { rbNodePoolByCtx.delete(ctx); } catch {}
      telemLogSnapshot('pool-clear');
    }
  } catch {}
  // Also clear the addModule guard map for this ctx so references can be GC'd
  try { loadedModulePromisesByCtx.delete(ctx); } catch {}
}

function getPoolMax(): number {
  // Default small to limit memory; can be overridden by env/localStorage
  let max = 1;
  try {
    const env = (process as any)?.env?.REACT_APP_MEDIA_RB_POOL_MAX;
    if (env != null && !Number.isNaN(Number(env))) max = Math.max(0, Math.floor(Number(env)));
  } catch {}
  try {
    const v = localStorage.getItem('MEDIA_RB_POOL_MAX');
    if (v != null && !Number.isNaN(Number(v))) max = Math.max(0, Math.floor(Number(v)));
  } catch {}
  return max;
}

function poolKeyForChannels(channels: number): string {
  const ch = Math.max(1, Math.min(32, (channels | 0) || 1));
  return `ch:${ch}`;
}

function acquireFromPool(ctx: AudioContext, channels: number): AudioWorkletNode | null {
  if (!isRbPoolEnabled()) return null;
  const m = poolMap(ctx);
  const key = poolKeyForChannels(channels);
  const arr = m.get(key) || [];
  if (arr.length) {
    const node = arr.shift()!;
    if (arr.length) m.set(key, arr); else m.delete(key);
    try { rbTelem.poolAcquire++; } catch {}
    try { telemUpdatePoolSize(key, arr.length); } catch {}
    telemLogSnapshot('acquire');
    const ch = rbNodeChannels.get(node) || channels;
    try { L.info('media:transport', 'rb.pool.acquire', { key, channels: ch }); } catch {}
    return node;
  }
  return null;
}

function releaseToPool(ctx: AudioContext, node: AudioWorkletNode) {
  if (!isRbPoolEnabled()) return;
  try {
    // Ask processor to free heavy engine state, but keep the shell (port stays open)
    try { node.port.postMessage({ type: 'dispose' }); telemBumpEngineDispose(node); } catch {}
    try { (node as any).__live = false; } catch {}
    try { node.disconnect(); } catch {}
    try { (node as any).onprocessorerror = null; } catch {}
    try { node.port.onmessage = null as any; } catch {}
    try { node.port.onmessageerror = null as any; } catch {}
    try {
      const stem: any = (node as any).__stemRef;
      if (stem?._rbOnLive) node.port.removeEventListener('message', stem._rbOnLive);
      delete (node as any).__stemRef;
    } catch {}
  } catch {}
  const m = poolMap(ctx);
  const ch = rbNodeChannels.get(node) || 1;
  const key = poolKeyForChannels(ch);
  const arr = m.get(key) || [];
  const MAX = getPoolMax();
  if (arr.length >= MAX) {
    // Pool full for this config: fully dispose extras
    try { rbTelem.poolFullDiscards++; rbTelem.fullDispose++; } catch {}
    try { fullyDisposeRbNode(node); } catch {}
    telemLogSnapshot('pool-full-discard');
    return;
  }
  arr.push(node);
  m.set(key, arr);
  try { rbTelem.poolRelease++; } catch {}
  try { telemUpdatePoolSize(key, arr.length); } catch {}
  telemLogSnapshot('release');
  try { L.info('media:transport', 'rb.pool.release', { key, channels: ch, size: arr.length }); } catch {}
}

/** Generic, idempotent addModule that ignores duplicates per ctx+url but surfaces real errors */
function addModuleGuarded(ctx: AudioContext, url: string): Promise<void> {
  let urlMap = loadedModulePromisesByCtx.get(ctx);
  if (!urlMap) {
    urlMap = new Map<string, Promise<void>>();
    loadedModulePromisesByCtx.set(ctx, urlMap);
  }
  const existing = urlMap.get(url);
  if (existing) return existing;
  const p = ctx.audioWorklet.addModule(url).catch((e: any) => {
    const msg = String(e?.message || '');
    const name = String(e?.name || '');
    // Ignore duplicates that some browsers surface as errors
    if (/already registered/i.test(msg) || name === 'NotSupportedError') return;
    // On other errors, remove from map so a future retry is possible
    urlMap!.delete(url);
    throw e;
  });
  urlMap.set(url, p);
  return p;
}

/** Load UMD first, then the processor, both into the AudioWorklet global scope */
async function ensureRubberBandModules(ctx: AudioContext): Promise<void> {
  // 1) UMD (defines globalThis.rubberband in AudioWorkletGlobalScope)
  await addModuleGuarded(ctx, '/wasm/rubberband.js');
  // 2) Processor: try primary path; only fallback if 404/Not Found
  try {
    await addModuleGuarded(ctx, '/worklets/rubberband-processor.js');
  } catch (e: any) {
    const msg = String(e?.message || '');
    if (/404|not\s*found/i.test(msg)) {
      // Optional alternate path if your deploy flattens assets
      await addModuleGuarded(ctx, '/wasm/rb/rubberband-processor.js');
    } else {
      throw e;
    }
  }
}

async function ensurePassthroughModule(ctx: AudioContext): Promise<void> {
  await addModuleGuarded(ctx, '/worklets/passthrough-processor.js');
}

async function ensureRbWasm(): Promise<ArrayBuffer> {
  if (!rbWasmBuffer) rbWasmBuffer = await loadRbWasm(); // loads /wasm/rubberband.wasm
  return rbWasmBuffer;
}

function cloneBuffer(buf: ArrayBuffer): ArrayBuffer {
  return buf.slice(0);
}

/** Reset worklet-side cached state (WASM buffer and last tempo/pitch globals). */
export function resetRbWorkletState() {
  try { rbWasmBuffer = null; } catch {}
  try { (globalThis as any).__rbLastTempo = undefined; } catch {}
  try { (globalThis as any).__rbLastPitch = undefined; } catch {}
}

/* ------------- Passthrough Worklet ------------- */
export async function ensurePassthroughWorklet(
  ctx: AudioContext,
  stems: Record<string, ScheduledStem>,
): Promise<void> {
  try {
    await ensurePassthroughModule(ctx);
  } catch (err) {
    console.error('[passthrough] failed to load module', err);
    return;
  }

  for (const [role, s] of Object.entries(stems)) {
    if (!s || s.worklet?.node) continue;

    try {
      const ch = Math.max(1, s.buffer.numberOfChannels || 1);
      const node = new AudioWorkletNode(ctx, 'passthrough-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [ch],
        channelCount: ch,
        channelCountMode: 'explicit',
        channelInterpretation: 'speakers',
      });

      s.worklet = { node };
      s.useWorklet = false; // passthrough
    } catch (err) {
      console.error(`[passthrough] failed for role=${role}:`, err);
      s.worklet = null;
      s.useWorklet = false;
    }
  }
}

/* -------- Rubber Band Worklet (time-stretch + pitch) -------- */
export async function ensureRubberBandWorklet(
  ctx: AudioContext,
  stems: Record<string, ScheduledStem>,
  init?: { config?: Record<string, any> },
): Promise<void> {
  if (!(await loadModulesAndWasm(ctx))) return;

  for (const [role, s] of Object.entries(stems)) {
    if (!s || s.worklet?.node) continue;
    try {
  let node = ctxOk(ctx) ? acquireFromPool(ctx, s.buffer.numberOfChannels) : null;
      if (!node) node = createRbNode(ctx, s.buffer.numberOfChannels);
      wireMessageHandlers(node, role);
      attachCrashGuards(ctx, stems, node, role);
      initRbNode(node, s.buffer.numberOfChannels, init);
      wrapWorklet(s, node);
      // Proactively connect node -> delay so sink path is ready before any source attaches
      try { if (ctxOk(ctx)) { safeConnectCtx(ctx, node, s.delay); } } catch {}
      // If a source is already playing, rewire it through the worklet immediately
      try {
        if (s.source) {
          try { s.source.disconnect(); } catch {}
          const actx = (s.source as any)?.context as BaseAudioContext | undefined;
          if (ctxOk(actx || null)) {
            if (safeConnectCtx(actx || null, s.source as any, node)) {
              safeConnectCtx(actx || null, node, s.delay);
            }
          }
        }
      } catch {}
    } catch (err) {
      console.error(`[rb] failed to init for role=${role}`, err);
      s.worklet = null;
      s.useWorklet = false;
    }
  }
}

/* ---------- Helpers ---------- */

async function loadModulesAndWasm(ctx: AudioContext): Promise<boolean> {
  try {
    await ensureRubberBandModules(ctx);
  } catch (err) {
    console.error('[rb] failed to load worklet modules', err);
    return false;
  }

  try {
    await ensureRbWasm();
  } catch (err) {
    console.error('[rb] failed to load wasm buffer', err);
    return false;
  }

  return true;
}

function createRbNode(ctx: AudioContext, channels: number): AudioWorkletNode {
  // Clamp channels to a reasonable upper bound for cross-browser stability
  const ch = Math.max(1, Math.min(32, (channels | 0) || 2));
  const node = new AudioWorkletNode(ctx, 'rubberband-processor', {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [ch],
    channelCount: ch,
    channelCountMode: 'explicit',
    channelInterpretation: 'speakers',
    // Provide initial channel info to processor constructor for early decisions
    processorOptions: { channels: ch },
  });
  // Double-assert channel configuration for cross-browser consistency
  try { (node as any).channelCountMode = 'explicit'; } catch {}
  try { (node as any).channelCount = ch; } catch {}
  try { (node as any).channelInterpretation = 'speakers'; } catch {}
  try { rbNodeChannels.set(node, ch); } catch {}
  return node;
}

/** Ensure any active sources are routed source -> RB node -> delay when RB is attached. */
export function reattachRbIfNeeded(ctx: AudioContext, stems: Record<string, any>) {
  try {
    for (const s of Object.values(stems || {})) {
      const stem: any = s;
      if (!stem?.source || !stem?.worklet?.node || !stem?.delay) continue;
      try { stem.source.disconnect(); } catch {}
      if (safeConnectCtx(ctx, stem.source as any, stem.worklet.node)) {
        safeConnectCtx(ctx, stem.worklet.node, stem.delay);
      }
    }
  } catch {}
}

function wireMessageHandlers(node: AudioWorkletNode, role: string) {
  node.port.onmessage = (ev) => {
    const { type, error, msg } = ev.data || {};
    if (type === 'engine-ready') {
      try { L.info('media:transport', 'rb.engine-ready', { role }); } catch {}
    } else if (type === 'engine-error') {
      console.error(`[rb] engine-error for role=${role}`, error);
      // Graceful fallback: detach RB node and rewire source directly
      try {
        const s: any = (node as any).__stemRef;
        const ctx: any = (node as any).context;
        if (s && ctxOk(ctx)) {
          try { s.useWorklet = false; } catch {}
          try { fullyDisposeRbNode(node, s); } catch {}
          try { if (s.source) { s.source.disconnect(); safeConnectCtx(ctx, s.source as any, s.delay); } } catch {}
          try { L.warn('media:transport', 'rb.fallback', { role }); } catch {}
        }
      } catch {}
    } else if (type === 'vm') {
      try { L.info('media:transport', 'rb.vm', { role, vm: ev.data?.vm, nodes: ev.data?.nodes }); } catch {}
    } else if (type === 'engines') {
      try { L.info('media:transport', 'rb.engines', { role, vm: ev.data?.vm, live: ev.data?.live, created: ev.data?.created, mode: ev.data?.mode }); } catch {}
    } else if (type === 'heap') {
      try { L.info('media:transport', 'rb.heap', { role, vm: ev.data?.vm, heap: ev.data?.heap }); } catch {}
    } else if (type === 'dbg') {
      // Lightweight debug messages (e.g., input sum) when telemetry is enabled
      try { L.info('media:transport', 'rb.dbg', { role, where: (ev as any)?.data?.where, s0: (ev as any)?.data?.s0 }); } catch {}
    } else if (type === 'live') {
      // Worklet reports it has observed non-zero input and is actively processing
      try { (node as any).__live = true; } catch {}
      try { L.info('media:transport', 'rb.live', { role }); } catch {}
    } else if (type === 'disposed') {
      try { (node as any).__live = false; } catch {}
      try { L.info('media:transport', 'rb.disposed', { role }); } catch {}
      try {
        const s: any = (node as any).__stemRef;
        if (s?._rbOnLive) node.port.removeEventListener('message', s._rbOnLive);
      } catch {}
    } else if (type === 'log') {
      // Avoid per-frame spam; throttle detailed logs
      try {
          const say = L.throttled('media:transport', `rb:${role}`, 1000);
    const sanitize = (v: any): any => {
            if (v == null) return v;
            if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
            if (Array.isArray(v)) return v.map(sanitize).slice(0, 16);
            if (typeof v === 'object') {
              const out: any = {};
              for (const k of Object.keys(v)) {
                const val = (v as any)[k];
                if (typeof val === 'function') continue;
                if (val && (val instanceof AudioWorkletNode || val instanceof MessagePort || val instanceof AudioNode)) continue;
                out[k] = sanitize(val);
              }
              return out;
            }
            return String(v);
          };
          if (msg === 'rb.fill') say('debug', 'rb.fill', sanitize(ev.data));
          else say('debug', String(msg));
      } catch {}
    } else if (type === 'playhead') {
      // Optional: lightweight playhead reporting for diagnostics
      // console.debug(`[rb:${role}] playhead`, ev.data?.samples);
    }
  };
}

function initRbNode(node: AudioWorkletNode, channels: number, init?: { config?: Record<string, any> }) {
  const bufCopy = cloneBuffer(rbWasmBuffer!); // cached in module
  // Try to read a global last tempo to include in init (best-effort)
  let initialTempo: number | undefined = undefined;
  let initialPitch: number | undefined = undefined;
  try { initialTempo = (globalThis as any).__rbLastTempo; } catch {}
  try { initialPitch = (globalThis as any).__rbLastPitch; } catch {}
  node.port.postMessage(
    {
      type: 'init',
      buffer: bufCopy,
      config: { channels: channels, initialTempo, initialPitch, ...(init?.config ?? {}) },
    },
    [bufCopy],
  );
  try { rbTelem.init++; } catch {}
  telemLogSnapshot('init');
  // Optional: enable worklet-side telemetry if flag set (for development diagnostics only)
  try {
    const t = localStorage.getItem('MEDIA_RB_TELEMETRY');
    if (t === '1' || String(t).toLowerCase() === 'true') {
      node.port.postMessage({ type: 'telemetry', enabled: true });
    }
  } catch {}
}

function wrapWorklet(s: ScheduledStem, node: AudioWorkletNode) {
  s.worklet = {
    node,
    setTempo: (value: number) => node.port.postMessage({ type: 'tempo', value }),
    setPitch: (value: number) => node.port.postMessage({ type: 'pitch', value }),
  } as WorkletWrapper;
  s.useWorklet = true;
  // Keep a back-reference for error handlers
  try { (node as any).__stemRef = s; } catch {}
  // Track liveness on the stem; flip to true once processor posts 'live'.
  (s as any)._rbLive = false;
  try {
    const onlive = (ev: MessageEvent) => {
      try {
        if ((ev as any)?.data?.type === 'live') { (s as any)._rbLive = true; }
      } catch {}
    };
    // Store handler for later removal on dispose
    (s as any)._rbOnLive = onlive;
    node.port.addEventListener('message', onlive as any);
  } catch {}
}

/* ----------------- Disconnect helper ----------------- */
export function disconnectAll(stems: Record<string, ScheduledStem>) {
  for (const s of Object.values(stems)) {
    if (!s) continue;
  try { s.source?.disconnect(); s.source = null; } catch {}
  try { fullyDisposeRbNode(s.worklet?.node, s); } catch {}
  try { s.delay?.disconnect(); } catch {}
  }
}

/**
 * Detach and dispose any active Rubber Band worklet nodes on stems, and reconnect sources directly.
 * Used when maintainPitch is turned OFF: removes processors to keep graph minimal.
 */
export function detachRubberBandWorklets(
  stems: Record<string, ScheduledStem>,
) {
  for (const [role, s] of Object.entries(stems)) {
    if (!s?.worklet?.node) { s.useWorklet = false; continue; }
    try {
      const node = s.worklet.node;
      // Prefer releasing to pool when enabled; otherwise fully dispose
      const actx = (node as any)?.context as AudioContext | undefined;
      if (isRbPoolEnabled() && actx) {
        if (ctxOk(actx || null)) {
          releaseToPool(actx, node);
        } else {
          // Context is closed; do not return node to pool tied to dead ctx
          fullyDisposeRbNode(node, s);
        }
      } else {
        fullyDisposeRbNode(node, s);
      }
    } catch {}
    // Rewire source to bypass worklet if currently connected through it
    try {
      if (s.source) {
        try { s.source.disconnect(); } catch {}
        const actx = (s.source as any)?.context as BaseAudioContext | undefined;
        if (ctxOk(actx || null)) {
          safeConnectCtx(actx || null, s.source as any, s.delay);
        }
      }
    } catch {}
    try { s.worklet = null as any; } catch {}
    try { s.useWorklet = false; } catch {}
    try { (s as any)._rbLive = false; } catch {}
    try { L.info('media:transport', 'rb.detach', { role }); } catch {}
  }
}

/* ----------------- Crash recovery ----------------- */
function attachCrashGuards(
  ctx: AudioContext,
  stems: Record<string, ScheduledStem>,
  node: AudioWorkletNode,
  role: string,
) {
  try { node.port.onmessageerror = (e: any) => { console.warn('[RB] messageerror', role, e); }; } catch {}
  try {
    (node as any).onprocessorerror = (e: any) => {
      console.error('[RB] processorerror', role, e);
      // Graceful fallback: detach RB node and route direct
      try {
        const s = stems[role] as any;
        if (s) {
          try { s.useWorklet = false; } catch {}
          try { fullyDisposeRbNode(node, s); } catch {}
          try { if (s.source) { s.source.disconnect(); safeConnectCtx(ctx, s.source as any, s.delay); } } catch {}
          try { L.warn('media:transport', 'rb.processorerror.fallback', { role }); } catch {}
        }
      } catch {}
    };
  } catch {}
}

export function recreateRubberBandForRole(
  ctx: AudioContext,
  stems: Record<string, ScheduledStem>,
  role: string,
) {
  const s = stems[role];
  if (!s) return;
  try {
    try { (s as any)._rbLive = false; } catch {}
    if (s.worklet?.node) {
      // Reuse existing processor in-place: reset engine & buffers
      try {
        const ch = Math.max(1, s.buffer.numberOfChannels || 1);
        // Optionally seed with last tempo/pitch globals
        let initialTempo: number | undefined = undefined;
        let initialPitch: number | undefined = undefined;
        try { initialTempo = (globalThis as any).__rbLastTempo; } catch {}
        try { initialPitch = (globalThis as any).__rbLastPitch; } catch {}
        s.worklet.node.port.postMessage({ type: 'reset', channels: ch, tempo: initialTempo ?? 1, pitch: initialPitch ?? 1 });
      } catch {}
    } else {
      // No node yet: create fresh
      const newNode = createRbNode(ctx, s.buffer.numberOfChannels);
      wireMessageHandlers(newNode, role);
      attachCrashGuards(ctx, stems, newNode, role);
      initRbNode(newNode, s.buffer.numberOfChannels);
      // swap wrapper
      wrapWorklet(s, newNode);
      // ensure connection sink is ready
      try { if (ctxOk(ctx)) { safeConnectCtx(ctx, newNode, s.delay); } } catch {}
    }
    // rewire active source if present
    try {
      if (s.source && s.worklet?.node) {
        // Keep existing wiring: source -> existing node -> delay
        // If source was bypassed, rewire through node
        try { s.source.disconnect(); } catch {}
        const actx = (s.source as any)?.context as BaseAudioContext | undefined;
        if (ctxOk(actx || null)) {
          if (safeConnectCtx(actx || null, s.source as any, s.worklet.node)) {
            safeConnectCtx(actx || null, s.worklet.node, s.delay);
          }
        }
      }
    } catch {}
  } catch (err) {
    console.error('[RB] recreate failed for role', role, err);
  }
}
